import React, { useEffect, useState } from "react";
import Sidebar from "../Sidebar/Sidebar";
import Navbar from "./Navbar";
import Header from "./header";
import { toast } from "react-toastify";
import { CircularProgress } from "@mui/material";
import ApiConfig from "../../common/config/ApiConfig";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import SaveAltIcon from "@mui/icons-material/SaveAlt";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import Step1 from "./PageComponents/CommissionReward/Step1";
import Step2 from "./PageComponents/CommissionReward/Step2";
import Step3 from "./PageComponents/CommissionReward/Step3";
import Step4 from "./PageComponents/CommissionReward/Step4";
import { useLanguage } from "../../common/i18n/LanguageContext";

function CommissionReward() {
  const { t } = useLanguage();
  const pos = localStorage.getItem("position");
  const [isThaiTableVisible] = useState(true);
  const [currentComponent, setCurrentComponent] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedLotto, setSelectedLotto] = useState("thai");
  const [lottoTypes, setLottoTypes] = useState([]);
  const [packages, setPackages] = useState([]);

  // Step 2 (bet limits) and Step 4 (fight limit) are kept per currency: the
  // owner picks a currency and edits that currency's own set of limits.
  // null until the owner's own currency is known (the default selection).
  const [currencies, setCurrencies] = useState([]);
  const [limitCurrencyId, setLimitCurrencyId] = useState(null);
  // Open/closed is separate from which currency is loaded, so the open card
  // can be collapsed without switching what's loaded.
  const [limitCardOpen, setLimitCardOpen] = useState(true);
  const limitCurrencyQuery = limitCurrencyId
    ? `&currency_id=${limitCurrencyId}`
    : "";

  useEffect(() => {
    const user = localStorage.getItem("user");
    Promise.all([
      ApiConfig.get("/getCurrencyList"),
      ApiConfig.get(`/agentprofile?user=${encodeURIComponent(user)}`),
    ]).then(([list, profile]) => {
      const available =
        list.resCode === 200 && Array.isArray(list.data)
          ? list.data.filter((c) => c.status)
          : [];
      setCurrencies(available);
      const ownCurrencyId =
        profile.resCode === 200 ? profile.data?.currency_id : null;
      setLimitCurrencyId(ownCurrencyId ?? available[0]?.id ?? 1);
    });
  }, []);

  // Maps LottoType.type (the raw string this page's API calls use, e.g.
  // "th_gov") to the short key each Step component's `show(key)` filter
  // expects — the two vocabularies predate each other and don't line up.
  const LOTTO_TYPE_KEY_BY_TYPE = {
    th_gov: "thai",
    hanoy_PhuQua: "hanoi",
    laos_gov: "laos",
    Yeekee_5: "yeekee",
    Yeekee_10: "yeekeevip",
    Yeekee_15: "yeekeevvip",
  };

  // Maps the short `selectedLotto` key to the raw LottoType.type
  // (buildRateObjFromGroups' first arg) and the field-name prefix used in
  // the /updateOwner body (th_maxbet, th_commission, ...) — used by
  // handleSubmit to send only the currently selected lotto type, since
  // /updateOwner now supports per-lotto-type partial updates (see
  // docs/updateOwner.md).
  const LOTTO_SECTION_BY_KEY = {
    thai: { raw: "th_gov", prefix: "th" },
    hanoi: { raw: "hanoy_PhuQua", prefix: "hanoi" },
    laos: { raw: "laos_gov", prefix: "laos" },
    yeekee: { raw: "Yeekee_5", prefix: "yeekee5" },
    yeekeevip: { raw: "Yeekee_10", prefix: "yeekee10" },
    yeekeevvip: { raw: "Yeekee_15", prefix: "yeekee15" },
  };

  const getLottoTypes = async () => {
    await ApiConfig.get("/getLottoTypes", {}).then((result) => {
      if (result.resCode !== 200) {
        toast.error(result.message || t("common_load_error"), {
          position: "top-right",
          autoClose: 3000,
        });
        return;
      }
      setLottoTypes(Array.isArray(result.data) ? result.data : []);
    });
  };

  const getPackages = async () => {
    await ApiConfig.get("/getPackageList").then((result) => {
      if (result.resCode !== 200) {
        toast.error(result.message || t("common_load_error"), {
          position: "top-right",
          autoClose: 3000,
        });
        return;
      }
      setPackages(Array.isArray(result.data) ? result.data : []);
    });
  };

  // All reward-type groups, every lotto type — fetched once. Step1/Step2's
  // display and the submit payload all derive their per-lotto-type slice
  // from this by filtering on group.lottoType.type, instead of re-fetching
  // per filter change or trusting a hardcoded stake list.
  const [rewardTypeGroups, setRewardTypeGroups] = useState([]);

  const getRewardTypeGroups = async () => {
    await ApiConfig.get("/getLottoRewardTypeGroups").then((result) => {
      if (result.resCode !== 200) {
        toast.error(result.message || t("common_load_error"), {
          position: "top-right",
          autoClose: 3000,
        });
        return;
      }
      setRewardTypeGroups(Array.isArray(result.data) ? result.data : []);
    });
  };

  // rewardTypes.code list for a given raw LottoType.type (e.g. "th_gov"),
  // across every group that belongs to it.
  const getRewardTypeCodes = (lottoTypeRaw) =>
    rewardTypeGroups
      .filter((g) => g.lottoType?.type === lottoTypeRaw)
      .flatMap((g) => (g.rewardTypes || []).map((rt) => rt.code));

  // Builds a commission/payrate/maxbet/minbet/maxbetpermatch submit object
  // from the reward-type codes that actually belong to this lotto type,
  // instead of forwarding whatever keys happen to be sitting in state. No
  // backend-side too — see member.service.ts), so read/write key is always
  // the bare code.
  const buildRateObjFromGroups = (lottoTypeRaw, stateObj) => {
    const codes = getRewardTypeCodes(lottoTypeRaw);
    const result = {};
    codes.forEach((code) => {
      result[code] = Number(stateObj[code] ?? 0);
    });
    return result;
  };

  const activeRewardTypeGroups = rewardTypeGroups.filter(
    (g) => LOTTO_TYPE_KEY_BY_TYPE[g.lottoType?.type] === selectedLotto,
  );

  // Step 1: Commission & Payrate — one independent set per package (Step1 is
  // rendered once per package card, toggled open/closed), keyed by package id
  // then by the short lotto key (thai/hanoi/laos/yeekee/yeekeevip/
  // yeekeevvip). /updateOwner has no upline-cap check (an owner has no
  // upline — see updateOwner.md), so unlike AgentAdd/AgentEdit's packagesData
  // there's no *Upline slot here.
  // packagesData[pkgId] = {
  //   enabled: boolean,             // true if this owner already has rows for this package
  //   commission: { thai: {...}, hanoi: {...}, ... },
  //   payrate: { thai: {...}, hanoi: {...}, ... },
  // }
  const [packagesData, setPackagesData] = useState({});

  const EMPTY_LOTTO_MAP = {
    thai: {},
    hanoi: {},
    laos: {},
    yeekee: {},
    yeekeevip: {},
    yeekeevvip: {},
  };
  const DEFAULT_PACKAGE_DATA = {
    enabled: false,
    commission: EMPTY_LOTTO_MAP,
    payrate: EMPTY_LOTTO_MAP,
  };
  const getPkgData = (pkgId) => packagesData[pkgId] ?? DEFAULT_PACKAGE_DATA;

  const setPackageEnabled = (pkgId, enabled) => {
    setPackagesData((prev) => {
      const current = prev[pkgId] ?? DEFAULT_PACKAGE_DATA;
      return {
        ...prev,
        [pkgId]: { ...current, enabled },
      };
    });
  };

  // Flips the switch immediately via PUT /updateagent/package/status
  // instead of waiting for the full form submit (see
  // docs/updateAgentPackageStatus.md) — optimistic update, reverted if the
  // request fails. A 404 means this package has never been saved for this
  // owner before (no AdminPackage row yet), so there's nothing to flip
  // until the full form is submitted at least once.
  const togglePackageEnabled = async (pkgId) => {
    const nextEnabled = !getPkgData(pkgId).enabled;
    setPackageEnabled(pkgId, nextEnabled);

    const user = localStorage.getItem("user");
    try {
      const result = await ApiConfig.put(
        `/updateagent/package/status/${user}/${pkgId}`,
        { status: nextEnabled },
      );
      if (result.resCode !== 200) {
        setPackageEnabled(pkgId, !nextEnabled);
        toast.error(result.message || t("common_load_error"), {
          position: "top-right",
          autoClose: 3000,
        });
      }
    } catch {
      setPackageEnabled(pkgId, !nextEnabled);
      toast.error(t("common_load_error"), {
        position: "top-right",
        autoClose: 3000,
      });
    }
  };

  // Whether a package's card body (Step1) is shown — a separate concern
  // from pkgData.enabled (the on/off switch, which controls the `status`
  // sent to the backend). Accordion behavior: at most one package expanded
  // at a time, defaulting to none (null) — opening one collapses whichever
  // was open before, and clicking the open one again closes it.
  const [expandedPackageId, setExpandedPackageId] = useState(null);
  const isPackageExpanded = (pkgId) => expandedPackageId === pkgId;
  const togglePackageExpanded = (pkgId) => {
    setExpandedPackageId((prev) => (prev === pkgId ? null : pkgId));
  };

  // Factory for Step1's setCommission/setPayrateUpdate props, scoped to one
  // package + one lotto key. Step1 calls these both as a plain new object
  // AND as a functional updater `(prev) => next` (its group-sync cascade
  // uses the latter), so this must support both call styles like a normal
  // useState setter does.
  const makePkgFieldSetter = (pkgId, field, lottoKey) => (valueOrFn) => {
    setPackagesData((prev) => {
      const current = prev[pkgId] ?? DEFAULT_PACKAGE_DATA;
      const currentValue = current[field][lottoKey] ?? {};
      const nextValue =
        typeof valueOrFn === "function" ? valueOrFn(currentValue) : valueOrFn;
      return {
        ...prev,
        [pkgId]: {
          ...current,
          [field]: { ...current[field], [lottoKey]: nextValue },
        },
      };
    });
  };

  // Step 2: Bet Limit
  const [Thaimaxbetupdate, setThaimaxbetupdate] = useState({});
  const [Thaiminbetupdate, setThaiminbetupdate] = useState({});
  const [Thaipermatchupdate, setThaipermatchupdate] = useState({});
  const [Hanoimaxbetupdate, setHanoimaxbetupdate] = useState({});
  const [Hanoiminbetupdate, setHanoiminbetupdate] = useState({});
  const [Hanoipermatchupdate, setHanoipermatchupdate] = useState({});
  const [Laosmaxbetupdate, setLaosmaxbetupdate] = useState({});
  const [Laosminbetupdate, setLaosminbetupdate] = useState({});
  const [Laospermatchupdate, setLaospermatchupdate] = useState({});
  const [Yeekeemaxbetupdate, setYeekeemaxbetupdate] = useState({});
  const [Yeekeeminbetupdate, setYeekeeminbetupdate] = useState({});
  const [Yeekeepermatchupdate, setYeekeepermatchupdate] = useState({});
  const [Yeekeevipmaxbetupdate, setYeekeevipmaxbetupdate] = useState({});
  const [Yeekeevipminbetupdate, setYeekeevipminbetupdate] = useState({});
  const [Yeekeevippermatchupdate, setYeekeevippermatchupdate] = useState({});
  const [Yeekeevvipmaxbetupdate, setYeekeevvipmaxbetupdate] = useState({});
  const [Yeekeevvipminbetupdate, setYeekeevvipminbetupdate] = useState({});
  const [Yeekeevvippermatchupdate, setYeekeevvippermatchupdate] = useState({});

  const [ThaiminbetUpline, setThaiminbetUpline] = useState({});
  const [HanoiminbetUpline, setHanoiminbetUpline] = useState({});
  const [LaosminbetUpline, setLaosminbetUpline] = useState({});
  const [YeekeeminbetUpline, setYeekeeminbetUpline] = useState({});
  const [YeekeevipminbetUpline, setYeekeevipminbetUpline] = useState({});
  const [YeekeevvipminbetUpline, setYeekeevvipminbetUpline] = useState({});

  // Step 3 & 4: Max Fight %
  const [Thaimaxfight, setThaimaxfight] = useState({});
  const [Hanoimaxfight, setHanoimaxfight] = useState({});
  const [Laosmaxfight, setLaosmaxfight] = useState({});
  const [Yeekeemaxfight, setYeekeemaxfight] = useState({});
  const [Yeekeevipmaxfight, setYeekeevipmaxfight] = useState({});
  const [Yeekeevvipmaxfight, setYeekeevvipmaxfight] = useState({});

  // Step 4: Fight Limit
  const [Thaimaxfightlimitupdate, setThaimaxfightlimitupdate] = useState({});
  const [Hanoimaxfightlimitupdate, setHanoimaxfightlimitupdate] = useState({});
  const [Laosmaxfightlimitupdate, setLaosmaxfightlimitupdate] = useState({});
  const [Yeekeemaxfightlimitupdate, setYeekeemaxfightlimitupdate] = useState(
    {},
  );
  const [Yeekeevipmaxfightlimitupdate, setYeekeevipmaxfightlimitupdate] =
    useState({});
  const [Yeekeevvipmaxfightlimitupdate, setYeekeevvipmaxfightlimitupdate] =
    useState({});

  const fightlimitget = async () => {
    const user = localStorage.getItem("user");
    await ApiConfig.get(
      `/getfightlimit?agent=${encodeURIComponent(user)}${limitCurrencyQuery}`,
    ).then((result) => {
      if (result.resCode !== 200) {
        toast.error(result.message || t("common_load_error"), {
          position: "top-right",
          autoClose: 3000,
        });
        return;
      }
      if (!result.data) return;

      // FightLimit rows are one row per (lotto_type, stake), e.g.
      // { lotto_type: "th_gov", stake: "first", fight_limit: "1000.00" } —
      // same shape as commission/payrate/maxbet, not one row per lotto_type
      // with named fields on it.
      const ownRows = Array.isArray(result.data?.fightlimt)
        ? result.data.fightlimt
        : [];
      const buildObj = (lottoTypeRaw) => {
        const built = {};
        getRewardTypeCodes(lottoTypeRaw).forEach((code) => {
          const row = ownRows.find(
            (obj) => obj.lotto_type === lottoTypeRaw && obj.stake === code,
          );
          built[code] = row ? Number(row.fight_limit) : 0;
        });
        return built;
      };

      setThaimaxfightlimitupdate(buildObj("th_gov"));
      setHanoimaxfightlimitupdate(buildObj("hanoy_PhuQua"));
      setLaosmaxfightlimitupdate(buildObj("laos_gov"));
      setYeekeemaxfightlimitupdate(buildObj("Yeekee_5"));
      setYeekeevipmaxfightlimitupdate(buildObj("Yeekee_10"));
      setYeekeevvipmaxfightlimitupdate(buildObj("Yeekee_15"));
    });
  };

  const maxbetget = async () => {
    const user = localStorage.getItem("user");
    await ApiConfig.get(
      `/Getmaxbet?agentuser=${encodeURIComponent(user)}${limitCurrencyQuery}`,
    ).then((result) => {
      if (result.resCode !== 200) {
        toast.error(result.message || t("common_load_error"), {
          position: "top-right",
          autoClose: 3000,
        });
        return;
      }
      if (!result.data) return;
      // MaxBet rows are one row per (lotto_type, stake), same shape as
      // commission/payrate/minbet.
      const ownRows = Array.isArray(result.data?.maxbet)
        ? result.data.maxbet
        : [];
      const buildObj = (lottoTypeRaw) => {
        const result = {};
        getRewardTypeCodes(lottoTypeRaw).forEach((code) => {
          const row = ownRows.find(
            (obj) => obj.lotto_type === lottoTypeRaw && obj.stake === code,
          );
          result[code] = row ? Number(row.maxbet) : 0;
        });
        return result;
      };

      setThaimaxbetupdate(buildObj("th_gov"));
      setHanoimaxbetupdate(buildObj("hanoy_PhuQua"));
      setLaosmaxbetupdate(buildObj("laos_gov"));
      setYeekeemaxbetupdate(buildObj("Yeekee_5"));
      setYeekeevipmaxbetupdate(buildObj("Yeekee_10"));
      setYeekeevvipmaxbetupdate(buildObj("Yeekee_15"));
    });
  };

  const minbetget = async () => {
    const user = localStorage.getItem("user");
    await ApiConfig.get(
      `/Getminbet?agentuser=${encodeURIComponent(user)}${limitCurrencyQuery}`,
    ).then((result) => {
      if (result.resCode !== 200) {
        toast.error(result.message || t("common_load_error"), {
          position: "top-right",
          autoClose: 3000,
        });
        return;
      }
      if (!result.data) return;
      // MinBet rows are one row per (lotto_type, stake), e.g.
      // { lotto_type: "th_gov", stake: "first", minbet: "10.00" } — same
      // shape as commission/payrate, not one row per lotto_type.
      const uplineRows = Array.isArray(result.data?.minbetUpline)
        ? result.data.minbetUpline
        : [];
      const ownRows = Array.isArray(result.data?.minbet)
        ? result.data.minbet
        : [];
      const getRow = (rows, lottoType, stake) => {
        const row = rows.find(
          (obj) => obj.lotto_type === lottoType && obj.stake === stake,
        );
        return row ? Number(row.minbet) : 0;
      };
      const buildObj = (lottoTypeRaw, rows) => {
        const result = {};
        getRewardTypeCodes(lottoTypeRaw).forEach((code) => {
          result[code] = getRow(rows, lottoTypeRaw, code);
        });
        return result;
      };

      setThaiminbetUpline(buildObj("th_gov", uplineRows));
      setThaiminbetupdate(buildObj("th_gov", ownRows));
      setHanoiminbetUpline(buildObj("hanoy_PhuQua", uplineRows));
      setHanoiminbetupdate(buildObj("hanoy_PhuQua", ownRows));
      setLaosminbetUpline(buildObj("laos_gov", uplineRows));
      setLaosminbetupdate(buildObj("laos_gov", ownRows));
      setYeekeeminbetUpline(buildObj("Yeekee_5", uplineRows));
      setYeekeeminbetupdate(buildObj("Yeekee_5", ownRows));
      setYeekeevipminbetUpline(buildObj("Yeekee_10", uplineRows));
      setYeekeevipminbetupdate(buildObj("Yeekee_10", ownRows));
      setYeekeevvipminbetUpline(buildObj("Yeekee_15", uplineRows));
      setYeekeevvipminbetupdate(buildObj("Yeekee_15", ownRows));
    });
  };

  const maxpermatchtget = async () => {
    const user = localStorage.getItem("user");
    await ApiConfig.get(
      `/Getmaxbetpermatch?agentuser=${encodeURIComponent(user)}${limitCurrencyQuery}`,
    ).then((result) => {
      if (result.resCode !== 200) {
        toast.error(result.message || t("common_load_error"), {
          position: "top-right",
          autoClose: 3000,
        });
        return;
      }
      if (!result.data) return;
      // MaxBetPerMatch rows are one row per (lotto_type, stake), same shape
      // as commission/payrate/minbet/maxbet.
      const ownRows = Array.isArray(result.data?.maxbetpermatch)
        ? result.data.maxbetpermatch
        : [];
      const buildObj = (lottoTypeRaw) => {
        const result = {};
        getRewardTypeCodes(lottoTypeRaw).forEach((code) => {
          const row = ownRows.find(
            (obj) => obj.lotto_type === lottoTypeRaw && obj.stake === code,
          );
          result[code] = row ? Number(row.maxbetpermatch) : 0;
        });
        return result;
      };

      setThaipermatchupdate(buildObj("th_gov"));
      setHanoipermatchupdate(buildObj("hanoy_PhuQua"));
      setLaospermatchupdate(buildObj("laos_gov"));
      setYeekeepermatchupdate(buildObj("Yeekee_5"));
      setYeekeevippermatchupdate(buildObj("Yeekee_10"));
      setYeekeevvippermatchupdate(buildObj("Yeekee_15"));
    });
  };

  const fightpercentandcommissionget = async (pkgId) => {
    const user = localStorage.getItem("user");
    const params = new URLSearchParams({ agent: user, package_id: pkgId });
    ApiConfig.get(`/getfightpercentandcommission?${params.toString()}`)
      .then((result) => {
        if (result.resCode !== 200) {
          toast.error(result.message || t("common_load_error"), {
            position: "top-right",
            autoClose: 3000,
          });
          return {
            commissionArray: [],
            payrateArray: [],
            fightpercentArray: [],
            adminPackageArray: [],
          };
        }
        const list = Array.isArray(result.data) ? result.data : [];
        const commissionArray = list.filter((obj) => "commission" in obj);
        const payrateArray = list.filter((obj) => "payrate" in obj);
        const fightpercentArray = list.filter((obj) => "fightpercent" in obj);
        // AdminPackage.status is the actual, current on/off state for this
        // package — not derivable from row existence, since commission/
        // payrate rows get written for every package regardless of status.
        const adminPackageArray = list.filter((obj) => "status" in obj);
        return {
          commissionArray,
          payrateArray,
          fightpercentArray,
          adminPackageArray,
        };
      })
      .then(
        ({
          commissionArray,
          payrateArray,
          fightpercentArray,
          adminPackageArray,
        }) => {
          if (!payrateArray?.[0] || !commissionArray?.[0]) return;

          // Commission and payrate rows are now one row per (lotto_type,
          // stake), e.g. { lotto_type: "th_gov", stake: "first", commission:
          // "30.00" } / { lotto_type: "th_gov", stake: "first", payrate:
          // "70000.00" }, not one row per lotto_type with named fields on it.
          // These are THIS package's own rows for the owner.
          const cmRows = Array.isArray(commissionArray[0]?.commission)
            ? commissionArray[0].commission
            : [];
          const getComm = (lottoType, stake) => {
            const row = cmRows.find(
              (obj) => obj.lotto_type === lottoType && obj.stake === stake,
            );
            return row ? Number(row.commission) : 0;
          };
          const prRows = Array.isArray(payrateArray[0]?.payrate)
            ? payrateArray[0].payrate
            : [];
          const getPay = (lottoType, stake) => {
            const row = prRows.find(
              (obj) => obj.lotto_type === lottoType && obj.stake === stake,
            );
            return row ? Number(row.payrate) : 0;
          };
          // Fight-percent rows are also one row per (lotto_type, stake), e.g.
          // { lotto_type: "th_gov", stake: "first", fight_percent_limit:
          // "90.00" }, not one row per lotto_type with named fields on it.
          const fpO = Array.isArray(fightpercentArray[0]?.fightpercent)
            ? fightpercentArray[0].fightpercent
            : [];
          const getFightPercent = (lottoType, stake) => {
            const row = fpO.find(
              (obj) => obj.lotto_type === lottoType && obj.stake === stake,
            );
            return row ? Number(row.fight_percent_limit) : 0;
          };

          // Populate every reward-type code that belongs to this lotto type
          // (not just the old fixed 11/6-stake list) — codes with no matching
          // row default to 0 via getComm/getPay.
          const buildFetchedRateObj = (lottoTypeRaw, getter) => {
            const result = {};
            getRewardTypeCodes(lottoTypeRaw).forEach((code) => {
              result[code] = getter(lottoTypeRaw, code);
            });
            return result;
          };

          // Step1's current values are package-scoped — write into this
          // package's own slot in packagesData, not shared top-level state.
          // `enabled` comes from the real AdminPackage.status returned by the
          // backend — NOT from whether commission rows exist, since rows get
          // written for every package regardless of its on/off status (a
          // disabled package still keeps its rows, just status: false), so
          // row existence can't tell on from off.
          setPackagesData((prev) => {
            const current = prev[pkgId] ?? DEFAULT_PACKAGE_DATA;
            return {
              ...prev,
              [pkgId]: {
                ...current,
                enabled: adminPackageArray[0]?.status ?? false,
                commission: {
                  thai: buildFetchedRateObj("th_gov", getComm),
                  hanoi: buildFetchedRateObj("hanoy_PhuQua", getComm),
                  laos: buildFetchedRateObj("laos_gov", getComm),
                  yeekee: buildFetchedRateObj("Yeekee_5", getComm),
                  yeekeevip: buildFetchedRateObj("Yeekee_10", getComm),
                  yeekeevvip: buildFetchedRateObj("Yeekee_15", getComm),
                },
                payrate: {
                  thai: buildFetchedRateObj("th_gov", getPay),
                  hanoi: buildFetchedRateObj("hanoy_PhuQua", getPay),
                  laos: buildFetchedRateObj("laos_gov", getPay),
                  yeekee: buildFetchedRateObj("Yeekee_5", getPay),
                  yeekeevip: buildFetchedRateObj("Yeekee_10", getPay),
                  yeekeevvip: buildFetchedRateObj("Yeekee_15", getPay),
                },
              },
            };
          });
          // Step3's fight-percent is NOT package-scoped (FightPercentLimit
          // has no package_id column) — shared top-level state is fine even
          // though this runs once per package (same value every time).
          setThaimaxfight(buildFetchedRateObj("th_gov", getFightPercent));
          setHanoimaxfight(
            buildFetchedRateObj("hanoy_PhuQua", getFightPercent),
          );
          setLaosmaxfight(buildFetchedRateObj("laos_gov", getFightPercent));
          setYeekeemaxfight(buildFetchedRateObj("Yeekee_5", getFightPercent));
          setYeekeevipmaxfight(
            buildFetchedRateObj("Yeekee_10", getFightPercent),
          );
          setYeekeevvipmaxfight(
            buildFetchedRateObj("Yeekee_15", getFightPercent),
          );
        },
      );
  };

  useEffect(() => {
    getLottoTypes();
    getRewardTypeGroups();
    getPackages();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // fightlimitget/maxbetget/minbetget/maxpermatchtget all build their state
  // from rewardTypeGroups' codes (see the buildObj helpers above), so they
  // have to wait for that fetch to land — calling them from the mount
  // effect above would close over rewardTypeGroups' still-empty initial
  // value and populate everything as 0.
  useEffect(() => {
    if (rewardTypeGroups.length > 0 && limitCurrencyId !== null) {
      fightlimitget();
      maxbetget();
      minbetget();
      maxpermatchtget();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rewardTypeGroups, limitCurrencyId]);

  // Fetch each package's own current commission/payrate + fight-percent
  // once the package list and reward groups are both ready — one call per
  // package (Step1 shows every package as its own card, not a single
  // selected one anymore).
  useEffect(() => {
    if (rewardTypeGroups.length > 0 && packages.length > 0) {
      packages.forEach((pkg) => fightpercentandcommissionget(pkg.id));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rewardTypeGroups, packages]);

  const handleChangeObj = (event, obj, setfunction) => {
    let maxvalue = event.target.value;
    if (maxvalue > 100) maxvalue = 100;
    setfunction({ ...obj, [event.target.name]: Number(maxvalue) });
  };

  const handleChangeFightLimit = (event, obj, setfunction) => {
    let maxvalue = event.target.value;
    if (maxvalue > 10000) maxvalue = 10000;
    setfunction({ ...obj, [event.target.name]: Number(maxvalue) });
  };

  const handleChangepayrateObj = (event, obj, setfunction) => {
    setfunction({ ...obj, [event.target.name]: Number(event.target.value) });
  };

  const handleNext = () => {
    if (currentComponent < 4) setCurrentComponent(currentComponent + 1);
  };

  const handlePrevious = () => {
    if (currentComponent > 1) setCurrentComponent(currentComponent - 1);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const { raw: activeRaw, prefix: activePrefix } =
      LOTTO_SECTION_BY_KEY[selectedLotto] ?? LOTTO_SECTION_BY_KEY.thai;

    // Only the section for the STEP currently being viewed is sent, scoped
    // to the currently selected lotto type — /updateOwner supports
    // per-lotto-type partial updates for every section (see
    // docs/updateOwner.md), including `packages[].commissions`/`payrates`
    // themselves being entirely optional, so switching to Step 2/3/4
    // doesn't require (or risk overwriting with stale state) Step 1's
    // commission/payrate data, and vice versa.
    const json = {
      // Every package is sent, not just the ones toggled on — a disabled
      // card still upserts its AdminPackage row with status: false, it's
      // just inactive. `id`/`status` go on every entry regardless of step;
      // `commissions`/`payrates` are only attached while Step 1 is open.
      packages: packages.map((pkg) => {
        const pkgData = getPkgData(pkg.id);
        const entry = { id: pkg.id, status: pkgData.enabled };
        if (currentComponent === 1) {
          entry.commissions = {
            [`${activePrefix}_commission`]: buildRateObjFromGroups(
              activeRaw,
              pkgData.commission[selectedLotto],
            ),
          };
          entry.payrates = {
            [`${activePrefix}_payrate`]: buildRateObjFromGroups(
              activeRaw,
              pkgData.payrate[selectedLotto],
            ),
          };
        }
        return entry;
      }),
    };

    if (currentComponent === 2 || currentComponent === 4) {
      json.limit_currency_id = limitCurrencyId;
    }
    if (currentComponent === 2) {
      json[`${activePrefix}_maxbet`] = buildRateObjFromGroups(
        activeRaw,
        activeBetLimit.maxbetUpdate,
      );
      json[`${activePrefix}_minbet`] = buildRateObjFromGroups(
        activeRaw,
        activeBetLimit.minbetUpdate,
      );
      json[`${activePrefix}_maxbetpermatch`] = buildRateObjFromGroups(
        activeRaw,
        activeBetLimit.maxbetpermatchUpdate,
      );
    }
    if (currentComponent === 3) {
      json[`${activePrefix}_maxpercent`] = buildRateObjFromGroups(
        activeRaw,
        activeMaxFight.maxfight,
      );
    }
    if (currentComponent === 4) {
      json[`${activePrefix}_fightlimit`] = buildRateObjFromGroups(
        activeRaw,
        activeFightLimit.fightlimit,
      );
    }

    setIsSubmitting(true);
    ApiConfig.post("/updateOwner", json)
      .then((result) => {
        if (result.resCode === 200) {
          toast.success(t("agentedit_save_success"), { autoClose: 2000 });
          packages.forEach((pkg) => fightpercentandcommissionget(pkg.id));
        } else {
          toast.error(result.message || t("agentedit_save_error"), {
            autoClose: 2000,
          });
        }
      })
      .finally(() => setIsSubmitting(false));
  };

  // Step 2 / Step 4 limits are per currency: like Step 1's package cards,
  // one collapsible card per currency. Only one is open at a time — the open
  // card is the one whose limits are loaded into (and saved from) the step's
  // form state, so opening another currency loads that currency's own limits.
  const renderCurrencyCards = (renderBody) => (
    <div className="flex flex-col gap-2">
      {currencies.map((c) => {
        const expanded = limitCardOpen && c.id === limitCurrencyId;
        return (
          <div
            key={c.id}
            className="w-full border-[1px] border-slate-300 rounded-lg bg-white overflow-hidden"
          >
            <div className="flex items-center justify-left px-3 py-2 bg-slate-100">
              <button
                type="button"
                onClick={() => {
                  if (c.id === limitCurrencyId) {
                    setLimitCardOpen((open) => !open);
                  } else {
                    setLimitCurrencyId(c.id);
                    setLimitCardOpen(true);
                  }
                }}
                className="flex items-center gap-1 text-[14px] font-bold"
              >
                {expanded ? (
                  <ExpandLessIcon style={{ fontSize: 20 }} />
                ) : (
                  <ExpandMoreIcon style={{ fontSize: 20 }} />
                )}
                {c.code} - {c.name} ({c.symbol})
              </button>
            </div>
            {expanded && <div className="p-2">{renderBody()}</div>}
          </div>
        );
      })}
    </div>
  );

  const STEPS = [
    { label: t("agentadd_step_commission_reward"), step: 1 },
    { label: t("agentadd_step_bet_limit"), step: 2 },
    { label: t("agentadd_step_max_fight_percent"), step: 3 },
    { label: t("agentadd_step_fight_limit"), step: 4 },
  ];

  // Step2's min/max/max-per-match bet limits only ever edit the currently
  // selected lotto type (their groups are already scoped to that type
  // below), so resolve which state set is "active" here instead of Step2
  // juggling all six. (Step1's commission/payrate are package-scoped now —
  // see packagesData above — so they don't need this treatment.)
  const ACTIVE_BETLIMIT_BY_KEY = {
    thai: {
      minbetUpdate: Thaiminbetupdate,
      setMinbetUpdate: setThaiminbetupdate,
      minbetUpline: ThaiminbetUpline,
      maxbetUpdate: Thaimaxbetupdate,
      setMaxbetUpdate: setThaimaxbetupdate,
      maxbetpermatchUpdate: Thaipermatchupdate,
      setMaxbetpermatchUpdate: setThaipermatchupdate,
    },
    hanoi: {
      minbetUpdate: Hanoiminbetupdate,
      setMinbetUpdate: setHanoiminbetupdate,
      minbetUpline: HanoiminbetUpline,
      maxbetUpdate: Hanoimaxbetupdate,
      setMaxbetUpdate: setHanoimaxbetupdate,
      maxbetpermatchUpdate: Hanoipermatchupdate,
      setMaxbetpermatchUpdate: setHanoipermatchupdate,
    },
    laos: {
      minbetUpdate: Laosminbetupdate,
      setMinbetUpdate: setLaosminbetupdate,
      minbetUpline: LaosminbetUpline,
      maxbetUpdate: Laosmaxbetupdate,
      setMaxbetUpdate: setLaosmaxbetupdate,
      maxbetpermatchUpdate: Laospermatchupdate,
      setMaxbetpermatchUpdate: setLaospermatchupdate,
    },
    yeekee: {
      minbetUpdate: Yeekeeminbetupdate,
      setMinbetUpdate: setYeekeeminbetupdate,
      minbetUpline: YeekeeminbetUpline,
      maxbetUpdate: Yeekeemaxbetupdate,
      setMaxbetUpdate: setYeekeemaxbetupdate,
      maxbetpermatchUpdate: Yeekeepermatchupdate,
      setMaxbetpermatchUpdate: setYeekeepermatchupdate,
    },
    yeekeevip: {
      minbetUpdate: Yeekeevipminbetupdate,
      setMinbetUpdate: setYeekeevipminbetupdate,
      minbetUpline: YeekeevipminbetUpline,
      maxbetUpdate: Yeekeevipmaxbetupdate,
      setMaxbetUpdate: setYeekeevipmaxbetupdate,
      maxbetpermatchUpdate: Yeekeevippermatchupdate,
      setMaxbetpermatchUpdate: setYeekeevippermatchupdate,
    },
    yeekeevvip: {
      minbetUpdate: Yeekeevvipminbetupdate,
      setMinbetUpdate: setYeekeevvipminbetupdate,
      minbetUpline: YeekeevvipminbetUpline,
      maxbetUpdate: Yeekeevvipmaxbetupdate,
      setMaxbetUpdate: setYeekeevvipmaxbetupdate,
      maxbetpermatchUpdate: Yeekeevvippermatchupdate,
      setMaxbetpermatchUpdate: setYeekeevvippermatchupdate,
    },
  };
  const activeBetLimit =
    ACTIVE_BETLIMIT_BY_KEY[selectedLotto] ?? ACTIVE_BETLIMIT_BY_KEY.thai;

  // Same idea as ACTIVE_BETLIMIT_BY_KEY above, for Step3's max fight
  // percent.
  const ACTIVE_MAXFIGHT_BY_KEY = {
    thai: { maxfight: Thaimaxfight, setMaxfight: setThaimaxfight },
    hanoi: { maxfight: Hanoimaxfight, setMaxfight: setHanoimaxfight },
    laos: { maxfight: Laosmaxfight, setMaxfight: setLaosmaxfight },
    yeekee: { maxfight: Yeekeemaxfight, setMaxfight: setYeekeemaxfight },
    yeekeevip: {
      maxfight: Yeekeevipmaxfight,
      setMaxfight: setYeekeevipmaxfight,
    },
    yeekeevvip: {
      maxfight: Yeekeevvipmaxfight,
      setMaxfight: setYeekeevvipmaxfight,
    },
  };
  const activeMaxFight =
    ACTIVE_MAXFIGHT_BY_KEY[selectedLotto] ?? ACTIVE_MAXFIGHT_BY_KEY.thai;

  // Same idea as ACTIVE_MAXFIGHT_BY_KEY above, for Step4's fight limit.
  const ACTIVE_FIGHTLIMIT_BY_KEY = {
    thai: {
      fightlimit: Thaimaxfightlimitupdate,
      setFightlimit: setThaimaxfightlimitupdate,
    },
    hanoi: {
      fightlimit: Hanoimaxfightlimitupdate,
      setFightlimit: setHanoimaxfightlimitupdate,
    },
    laos: {
      fightlimit: Laosmaxfightlimitupdate,
      setFightlimit: setLaosmaxfightlimitupdate,
    },
    yeekee: {
      fightlimit: Yeekeemaxfightlimitupdate,
      setFightlimit: setYeekeemaxfightlimitupdate,
    },
    yeekeevip: {
      fightlimit: Yeekeevipmaxfightlimitupdate,
      setFightlimit: setYeekeevipmaxfightlimitupdate,
    },
    yeekeevvip: {
      fightlimit: Yeekeevvipmaxfightlimitupdate,
      setFightlimit: setYeekeevvipmaxfightlimitupdate,
    },
  };
  const activeFightLimit =
    ACTIVE_FIGHTLIMIT_BY_KEY[selectedLotto] ?? ACTIVE_FIGHTLIMIT_BY_KEY.thai;

  return (
    <div className="h-screen flex flex-col overflow-hidden">
      <div className="shrink-0">
        <Navbar />
      </div>
      <div className="flex flex-1 overflow-hidden items-start">
        <div className="shrink-0 overflow-y-auto h-full">
          <Sidebar pos={pos} />
        </div>
        <div className="flex flex-col flex-1 max-h-full overflow-hidden">
          <div className="shrink-0">
            <Header title={t("menu_commission_reward_setting")} />
          </div>
          <div className="flex-1 overflow-y-auto mb-10">
            <div className="flex items-center justify-center mb-3 mt-2">
              <label className="text-[13px] font-bold mr-2">
                {t("member_commission_filter_label")}
              </label>
              <select
                value={selectedLotto}
                onChange={(e) => setSelectedLotto(e.target.value)}
                className="border-[1px] border-slate-300 rounded-lg text-[13px] font-bold px-2 py-1"
              >
                {lottoTypes
                  .filter((lt) => LOTTO_TYPE_KEY_BY_TYPE[lt.type])
                  .map((lt) => (
                    <option key={lt.id} value={LOTTO_TYPE_KEY_BY_TYPE[lt.type]}>
                      {lt.name}
                    </option>
                  ))}
              </select>
            </div>
            <div className="stepper flex items-center justify-center mb-3">
              {STEPS.map(({ label, step }, idx, arr) => (
                <React.Fragment key={step}>
                  <div className="flex flex-col items-center">
                    <button
                      type="button"
                      onClick={() => setCurrentComponent(step)}
                      className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold border-2 transition-colors ${
                        currentComponent === step
                          ? "bg-brand-blue-600 border-brand-blue-600 text-white"
                          : currentComponent > step
                            ? "bg-brand-blue-300 border-brand-blue-300 text-white"
                            : "bg-white border-gray-300 text-gray-400"
                      }`}
                    >
                      {step}
                    </button>
                    <span
                      className={`text-[11px] mt-1 text-center whitespace-nowrap ${
                        currentComponent === step
                          ? "text-brand-blue-600 font-bold"
                          : currentComponent > step
                            ? "text-brand-blue-400"
                            : "text-gray-400"
                      }`}
                    >
                      {label}
                    </span>
                  </div>
                  {idx < arr.length - 1 && (
                    <div
                      className={`h-[2px] w-12 mb-4 mx-1 transition-colors ${
                        currentComponent > step
                          ? "bg-brand-blue-400"
                          : "bg-gray-300"
                      }`}
                    />
                  )}
                </React.Fragment>
              ))}
            </div>
            <form onSubmit={handleSubmit}>
              {currentComponent === 1 && (
                <div className="flex flex-col gap-2">
                  {packages.map((pkg) => {
                    const pkgData = getPkgData(pkg.id);
                    const pkgName =
                      t(pkg.code) !== pkg.code ? t(pkg.code) : pkg.name;
                    return (
                      <div
                        key={pkg.id}
                        className="w-full border-[1px] border-slate-300 rounded-lg bg-white overflow-hidden"
                      >
                        <div className="flex items-center justify-left px-3 py-2 bg-slate-100">
                          <button
                            type="button"
                            onClick={() => togglePackageExpanded(pkg.id)}
                            className="flex items-center gap-1 text-[14px] font-bold"
                          >
                            {isPackageExpanded(pkg.id) ? (
                              <ExpandLessIcon style={{ fontSize: 20 }} />
                            ) : (
                              <ExpandMoreIcon style={{ fontSize: 20 }} />
                            )}
                            {pkgName}
                          </button>
                          <label className="inline-flex items-center cursor-pointer ml-4">
                            <input
                              type="checkbox"
                              className="sr-only peer ml-2"
                              checked={pkgData.enabled}
                              onChange={() => togglePackageEnabled(pkg.id)}
                            />
                            <div className="relative w-10 h-5 bg-gray-300 rounded-full peer-checked:bg-green-600 transition-colors">
                              <div
                                className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${
                                  pkgData.enabled ? "translate-x-5" : ""
                                }`}
                              />
                            </div>
                          </label>
                        </div>
                        {isPackageExpanded(pkg.id) && (
                          <div className="p-2">
                            <Step1
                              isThaiTableVisible={isThaiTableVisible}
                              rewardTypeGroups={activeRewardTypeGroups}
                              payrateUpdate={
                                pkgData.payrate[selectedLotto] ?? {}
                              }
                              setPayrateUpdate={makePkgFieldSetter(
                                pkg.id,
                                "payrate",
                                selectedLotto,
                              )}
                              commission={
                                pkgData.commission[selectedLotto] ?? {}
                              }
                              setCommission={makePkgFieldSetter(
                                pkg.id,
                                "commission",
                                selectedLotto,
                              )}
                              handleChangeObj={handleChangeObj}
                              handleChangepayrateObj={handleChangepayrateObj}
                            />
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
              {currentComponent === 2 &&
                renderCurrencyCards(() => (
                  <Step2
                    isThaiTableVisible={isThaiTableVisible}
                    rewardTypeGroups={activeRewardTypeGroups}
                    minbetUpdate={activeBetLimit.minbetUpdate}
                    setMinbetUpdate={activeBetLimit.setMinbetUpdate}
                    minbetUpline={activeBetLimit.minbetUpline}
                    maxbetUpdate={activeBetLimit.maxbetUpdate}
                    setMaxbetUpdate={activeBetLimit.setMaxbetUpdate}
                    maxbetpermatchUpdate={activeBetLimit.maxbetpermatchUpdate}
                    setMaxbetpermatchUpdate={
                      activeBetLimit.setMaxbetpermatchUpdate
                    }
                    handleChangepayrateObj={handleChangepayrateObj}
                  />
                ))}
              {currentComponent === 3 && (
                <Step3
                  isThaiTableVisible={isThaiTableVisible}
                  rewardTypeGroups={activeRewardTypeGroups}
                  maxfight={activeMaxFight.maxfight}
                  setMaxfight={activeMaxFight.setMaxfight}
                  handleChangeObj={handleChangeObj}
                />
              )}
              {currentComponent === 4 &&
                renderCurrencyCards(() => (
                  <Step4
                    isThaiTableVisible={isThaiTableVisible}
                    rewardTypeGroups={activeRewardTypeGroups}
                    fightlimit={activeFightLimit.fightlimit}
                    setFightlimit={activeFightLimit.setFightlimit}
                    handleChangeObj={handleChangeFightLimit}
                  />
                ))}
              <div className="flex flex-row space-x-2 mb-1 mt-3">
                {currentComponent < 4 && (
                  <button
                    type="button"
                    onClick={handleNext}
                    className="w-[100%] h-[2rem] bg-brand-blue-600 mr-1 hover:bg-brand-blue-700 text-white text-[14px] font-bold py-1 px-2 rounded flex items-center justify-center gap-1"
                  >
                    {t("common_next")}{" "}
                    <ArrowForwardIosIcon style={{ fontSize: 20 }} />
                  </button>
                )}
                {currentComponent !== 1 && (
                  <button
                    type="button"
                    onClick={handlePrevious}
                    className="w-[100%] h-[2rem] bg-brand-blue-600 mr-1 hover:bg-brand-blue-700 text-white text-[14px] font-bold py-1 px-2 rounded flex items-center justify-center gap-1"
                  >
                    <ArrowBackIosNewIcon style={{ fontSize: 20 }} />{" "}
                    {t("common_previous")}
                  </button>
                )}
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-[100%] h-[2rem] bg-green-600 mr-1 hover:bg-green-700 disabled:opacity-60 disabled:cursor-not-allowed text-white text-[14px] font-bold py-1 px-2 rounded flex items-center justify-center gap-1"
                >
                  {isSubmitting ? (
                    <>
                      <CircularProgress size={16} sx={{ color: "white" }} />
                      {t("common_saving")}
                    </>
                  ) : (
                    <>
                      <SaveAltIcon style={{ fontSize: 20 }} />{" "}
                      {t("agentadd_save_button")}
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}

export default CommissionReward;
